/**
 * The unit registry: the single source of truth ({@link UnitDef}) mapping declared
 * unit slugs to their canonical-base dimension vector, Unicode display string, and
 * per-unit numeric conversion functions ({@link UnitDef.toBase}/{@link UnitDef.fromBase}).
 *
 * Every physical quantity has exactly ONE canonical base unit (its `toBase` is the
 * identity). All other units of that quantity — including magnitude-scaled ladder
 * rungs (MSTB, MMSCF, BCF, …) — are {@link UnitDef}s over the same base dimension,
 * so any-to-any conversion is automatic via the base and same-dimension values
 * cancel/convert in the dimension algebra.
 *
 * The **slug** is the public, declared, serializable identity; the {@link Unit}
 * dimension vector is the internal propagation representation. Display laddering
 * (selecting a human-readable rung for a base value) is driven by {@link pickDisplay}
 * over the {@link LADDERS}.
 */

import { type ColumnType, displayUnitOfType } from '../expr/schema/types.ts';
import {
  type BaseSlug,
  dimensionless,
  equals,
  isDimensionless,
  multiply,
  reciprocal,
  type Unit,
  unitOf,
} from './unit.ts';

/** Slug identifying a declared unit — the public vocabulary surfaced by `describeViews`. */
export type UnitSlug = string;

/**
 * A declared unit: its canonical-base dimension vector plus the per-unit functions
 * mapping a value to and from the canonical base of that dimension.
 */
export interface UnitDef {
  /** Public, serializable id, e.g. `mmscf_per_d`. */
  readonly slug: UnitSlug;
  /** Human-readable display string, e.g. `MMSCF/d`. */
  readonly display: string;
  /** Canonical-base dimension vector (base slug → exponent), e.g. `{ scf: 1, d: -1 }`. */
  readonly dimension: Unit;
  /** Maps a value in THIS unit to the canonical base of its dimension; identity for a base unit. */
  readonly toBase: (v: number) => number;
  /** Inverse of {@link toBase}. */
  readonly fromBase: (v: number) => number;
  /** Optional id of the display ladder this unit belongs to. */
  readonly ladder?: string;
}

const identity = (v: number): number => v;

/** A linear scale factor: `toBase` multiplies, `fromBase` divides. */
const scale = (factor: number): Pick<UnitDef, 'toBase' | 'fromBase'> => ({
  toBase: (v) => v * factor,
  fromBase: (v) => v / factor,
});

/**
 * A canonical base unit: its dimension is `{ base: 1 }` and conversion is the
 * identity. Exported (with {@link scaled}) as the constructor vocabulary for
 * app-registered definitions ({@link registerUnitDefs}).
 */
export const base = (slug: UnitSlug, display: string, baseSlug: BaseSlug = slug): UnitDef => ({
  slug,
  display,
  dimension: unitOf([[baseSlug, 1]]),
  toBase: identity,
  fromBase: identity,
});

/** A linearly-scaled unit over a base dimension (e.g. a ladder rung). */
export const scaled = (
  slug: UnitSlug,
  display: string,
  dimension: readonly (readonly [BaseSlug, number])[],
  factor: number,
  ladder?: string,
): UnitDef => ({ slug, display, dimension: unitOf(dimension), ...scale(factor), ...(ladder ? { ladder } : {}) });

/** Petroleum-prefix steps shared by every laddered family: x1, x1e3, x1e6, x1e9, x1e12. */
const LIQUID_VOL = [['stb', 1]] as const;
const GAS_VOL = [['scf', 1]] as const;
const PER_D = [['d', -1]] as const;

const liquidRate = [...LIQUID_VOL, ...PER_D];
const gasRate = [...GAS_VOL, ...PER_D];

/** liquid cumulative-volume ladder (no /d). */
const LIQUID_VOLUME_LADDER: readonly UnitDef[] = [
  scaled('stb', 'STB', LIQUID_VOL, 1, 'liquid-volume'),
  scaled('mstb', 'MSTB', LIQUID_VOL, 1e3, 'liquid-volume'),
  scaled('mmstb', 'MMSTB', LIQUID_VOL, 1e6, 'liquid-volume'),
];

/** liquid-rate ladder (/d). */
const LIQUID_RATE_LADDER: readonly UnitDef[] = [
  scaled('stb_per_d', 'STB/d', liquidRate, 1, 'liquid-rate'),
  scaled('mstb_per_d', 'MSTB/d', liquidRate, 1e3, 'liquid-rate'),
  scaled('mmstb_per_d', 'MMSTB/d', liquidRate, 1e6, 'liquid-rate'),
];

/** gas cumulative-volume ladder (no /d). */
const GAS_VOLUME_LADDER: readonly UnitDef[] = [
  scaled('scf', 'SCF', GAS_VOL, 1, 'gas-volume'),
  scaled('mscf', 'MSCF', GAS_VOL, 1e3, 'gas-volume'),
  scaled('mmscf', 'MMSCF', GAS_VOL, 1e6, 'gas-volume'),
  scaled('bcf', 'BCF', GAS_VOL, 1e9, 'gas-volume'),
  scaled('tcf', 'TCF', GAS_VOL, 1e12, 'gas-volume'),
];

/** gas-rate ladder (/d). */
const GAS_RATE_LADDER: readonly UnitDef[] = [
  scaled('scf_per_d', 'SCF/d', gasRate, 1, 'gas-rate'),
  scaled('mscf_per_d', 'MSCF/d', gasRate, 1e3, 'gas-rate'),
  scaled('mmscf_per_d', 'MMSCF/d', gasRate, 1e6, 'gas-rate'),
  scaled('bcf_per_d', 'BCF/d', gasRate, 1e9, 'gas-rate'),
  scaled('tcf_per_d', 'TCF/d', gasRate, 1e12, 'gas-rate'),
];

/** Display ladders keyed by ladder id; each is an ordered list of rungs (smallest → largest). */
export const LADDERS: Readonly<Record<string, readonly UnitDef[]>> = {
  'liquid-volume': LIQUID_VOLUME_LADDER,
  'liquid-rate': LIQUID_RATE_LADDER,
  'gas-volume': GAS_VOLUME_LADDER,
  'gas-rate': GAS_RATE_LADDER,
};

const LADDER_RUNGS: readonly UnitDef[] = Object.values(LADDERS).flat();

/** Temperature in Fahrenheit over the °C canonical base (affine). */
const degf: UnitDef = {
  slug: 'degf',
  display: '°F',
  dimension: unitOf([['degc', 1]]),
  toBase: (v) => ((v - 32) * 5) / 9,
  fromBase: (v) => (v * 9) / 5 + 32,
};

/** Standalone (non-ladder) units: bases and derived combinations carried by columns. */
const STANDALONE: readonly UnitDef[] = [
  // Pressure (psi base; psig/psia are gauge/absolute labels over the same dimension).
  base('psi', 'psi'),
  base('psig', 'psig', 'psi'),
  base('psia', 'psia', 'psi'),
  // Temperature.
  base('degc', '°C'),
  degf,
  // Length & velocity. Inches are deliberately their OWN base — choke sizes
  // are quoted in inches; folding them into the ft dimension would either
  // rescale stored values or display chokes in feet.
  base('ft', 'ft'),
  base('in', 'in'),
  scaled(
    'ft_per_s',
    'ft/s',
    [
      ['ft', 1],
      ['s', -1],
    ],
    1,
  ),
  base('s', 's'),
  // Frequency, money, power, electricals.
  base('hz', 'Hz'),
  base('usd', '$'),
  base('hp', 'HP'),
  base('a', 'A'),
  base('v', 'V'),
  base('kva', 'kVA'),
  base('d', 'd'),
  // Energy & mass (energy-intensity source units: GJ/MT, MWh/MT, …).
  // Deliberately independent bases — MWh is NOT declared convertible to GJ:
  // datapoints are carried verbatim in their stored unit (no ingest
  // conversion), so convertibility would claim a relation the values
  // don't satisfy.
  base('gj', 'GJ'),
  base('mwh', 'MWh'),
  base('mt', 'MT'),
  // Derived ratios.
  scaled(
    'scf_per_stb',
    'scf/STB',
    [
      ['scf', 1],
      ['stb', -1],
    ],
    1,
  ),
  scaled(
    'stb_per_mmscf',
    'STB/MMSCF',
    [
      ['stb', 1],
      ['scf', -1],
    ],
    1e-6,
  ),
  scaled(
    'stb_per_d_psi',
    'STB/d/psi',
    [
      ['stb', 1],
      ['d', -1],
      ['psi', -1],
    ],
    1,
  ),
  scaled(
    'usd_per_stb',
    '$/STB',
    [
      ['usd', 1],
      ['stb', -1],
    ],
    1,
  ),
  base('bbl', 'bbl'),
  scaled(
    'usd_per_bbl',
    '$/bbl',
    [
      ['usd', 1],
      ['bbl', -1],
    ],
    1,
  ),
  // Dimensionless representations (forward-only).
  { slug: 'pct', display: '%', dimension: dimensionless, toBase: identity, fromBase: identity },
  { slug: 'fraction', display: '', dimension: dimensionless, toBase: identity, fromBase: identity },
];

/** Every declared unit: standalone units first, then ladder rungs. */
const DEFINITIONS: readonly UnitDef[] = [...STANDALONE, ...LADDER_RUNGS];

const CORE_BY_SLUG: ReadonlyMap<UnitSlug, UnitDef> = new Map(DEFINITIONS.map((d) => [d.slug, d]));

/** Resolve a declared slug to its full {@link UnitDef}, if registered. */
export const unitDefForSlug = (slug: UnitSlug): UnitDef | undefined => registry.bySlug.get(slug);

/** Deterministic key for a dimension vector, for reverse (unit → slug/display) lookup. */
const signature = (unit: Unit): string =>
  [...unit]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([slug, exponent]) => `${slug}:${exponent}`)
    .join(',');

/**
 * The canonical display unit per dimension: the standard (imperial) ladder rung that
 * names a base value, or a registered standalone unit. Listed in priority order so the
 * first match per signature wins (e.g. `psig` over `psi`/`psia`; the base rung names a
 * cumulative-volume dimension, the `/d` base rung names a rate dimension).
 */
const CANONICAL_BY_DIMENSION: readonly UnitDef[] = [
  CORE_BY_SLUG.get('psig') as UnitDef,
  ...STANDALONE,
  CORE_BY_SLUG.get('stb') as UnitDef,
  CORE_BY_SLUG.get('scf') as UnitDef,
  CORE_BY_SLUG.get('stb_per_d') as UnitDef,
  CORE_BY_SLUG.get('scf_per_d') as UnitDef,
];

const canonicalDimensioned = CANONICAL_BY_DIMENSION.filter((d) => !isDimensionless(d.dimension));
type RegistryState = {
  bySlug: ReadonlyMap<UnitSlug, UnitDef>;
  byAlias: ReadonlyMap<string, UnitSlug>;
  slugBySignature: ReadonlyMap<string, UnitSlug>;
  displayBySignature: ReadonlyMap<string, string>;
};

/**
 * Live registry state: the core vocabulary plus app-registered extensions
 * ({@link registerUnitDefs}). Registration swaps whole map bindings — the maps
 * themselves are never mutated — so every reader sees a consistent snapshot.
 */
const registry: RegistryState = {
  bySlug: CORE_BY_SLUG,
  byAlias: new Map(),
  slugBySignature: new Map([...canonicalDimensioned].reverse().map((d) => [signature(d.dimension), d.slug])),
  displayBySignature: new Map([...canonicalDimensioned].reverse().map((d) => [signature(d.dimension), d.display])),
};

/**
 * Extend the vocabulary with app-domain definitions and source-string aliases
 * (alias → declared slug) at bootstrap, before any dataset resolution. Core
 * definitions win on collision, so an app can neither redefine a declared slug
 * nor override a dimension's canonical display. This is the one stateful seam
 * in the units layer — threading a registry instance through every Unit
 * consumer would buy purity at the cost of the whole call graph.
 */
export const registerUnitDefs = (defs: readonly UnitDef[], aliases?: Readonly<Record<string, UnitSlug>>): void => {
  const dimensioned = defs.filter((d) => !isDimensionless(d.dimension));
  registry.bySlug = new Map([...defs.map((d) => [d.slug, d] as const), ...registry.bySlug]);
  registry.byAlias = new Map([...Object.entries(aliases ?? {}), ...registry.byAlias]);
  registry.slugBySignature = new Map([
    ...dimensioned.map((d) => [signature(d.dimension), d.slug] as const),
    ...registry.slugBySignature,
  ]);
  registry.displayBySignature = new Map([
    ...dimensioned.map((d) => [signature(d.dimension), d.display] as const),
    ...registry.displayBySignature,
  ]);
};

/** Resolve a declared slug to its dimension vector; `undefined` for an unknown slug. */
export const unitForSlug = (slug: UnitSlug): Unit | undefined => registry.bySlug.get(slug)?.dimension;

/** Whether a def's to-base conversion is the identity — safe to stamp on values that are never converted. */
const isIdentityDef = (def: UnitDef): boolean => def.toBase(0) === 0 && def.toBase(1) === 1;

/** Resolve one source token: exact declared slug, then declared alias, then case-folded slug. */
const defForToken = (token: string): UnitDef | undefined => {
  const exact = registry.bySlug.get(token);
  if (exact !== undefined) {
    return exact;
  }
  const aliased = registry.byAlias.get(token);
  if (aliased !== undefined) {
    return registry.bySlug.get(aliased);
  }
  return registry.bySlug.get(token.toLowerCase());
};

/**
 * Conservative compound parse of `A/B/…`: every token must resolve to an
 * identity-conversion def, otherwise no parse. The first token is the
 * numerator, every later token divides — matching the declared
 * `stb_per_d_psi` = "STB/d/psi" convention.
 */
const parseCompound = (normalized: string): Unit | undefined => {
  const tokens = normalized.split('/').map((t) => t.trim());
  const defs = tokens.map(defForToken).filter((d): d is UnitDef => d !== undefined && isIdentityDef(d));
  if (tokens.length < 2 || defs.length !== tokens.length) {
    return undefined;
  }
  return defs.reduce<Unit>(
    (acc, def, i) => multiply(acc, i === 0 ? def.dimension : reciprocal(def.dimension)),
    dimensionless,
  );
};

/**
 * Resolve a source-system unit string to a dimension vector, degrading
 * capability but never dropping the unit:
 *
 *   1. registered slug or alias (case-folded) → its declared dimension,
 *   2. conservative compound parse (`GJ/MT` → `{gj: 1, mt: -1}`),
 *   3. otherwise the whole string becomes ONE opaque base dimension — a
 *      display-only unit that is compared, unified and surfaced verbatim,
 *      never converted.
 *
 * Tiers 1–2 accept only identity-conversion defs: this resolver stamps units
 * onto values carried VERBATIM (no ingest conversion), so a scaled or affine
 * def (`mscf`, `degf`) would claim a dimension the numbers don't satisfy —
 * such strings go opaque instead. Only a blank string resolves to no unit.
 */
export const resolveSourceUnit = (raw: string): Unit | undefined => {
  const normalized = raw.trim().replace(/\s+/g, ' ');
  if (normalized === '') {
    return undefined;
  }
  const direct = defForToken(normalized);
  if (direct !== undefined && isIdentityDef(direct)) {
    return direct.dimension;
  }
  return parseCompound(normalized) ?? unitOf([[normalized, 1]]);
};

/** Display string for a declared slug (e.g. `stb_per_d` → "STB/d"). */
export const displayForSlug = (slug: UnitSlug): string => registry.bySlug.get(slug)?.display ?? '';

/** The canonical declared slug for a dimension vector, if one is registered. */
export const slugForUnit = (unit: Unit): UnitSlug | undefined => registry.slugBySignature.get(signature(unit));

/**
 * Display the declared unit of a column type, or blank when the type carries
 * no unit. Uses {@link displayUnitOfType}, so a timeseries-reference column
 * surfaces the unit of the datapoint scalar it carries (its inner `value`
 * leaf); {@link formatUnit} renders registered dimensions canonically and
 * composes unregistered/opaque ones generically.
 */
export const displaySymbolForType = (type: ColumnType): string => {
  const unit = displayUnitOfType(type);
  return unit === undefined ? '' : formatUnit(unit);
};

const SUPERSCRIPTS: Readonly<Record<string, string>> = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
  '-': '⁻',
};

/** Render an exponent as Unicode superscript; the implicit exponent 1 renders as "". */
const superscript = (n: number): string => (n === 1 ? '' : [...String(n)].map((ch) => SUPERSCRIPTS[ch] ?? ch).join(''));

/**
 * Compose a display for a unit with no registered signature, e.g.
 * `{scf: 1, stb: -1}` → "SCF/STB". A base token displays via its registered
 * def when one exists; an opaque source-string base displays verbatim.
 */
const composeGeneric = (unit: Unit): string => {
  const token = ([slug, exponent]: readonly [BaseSlug, number]): string =>
    `${registry.bySlug.get(slug)?.display ?? slug}${superscript(Math.abs(exponent))}`;
  const sorted = [...unit].sort(([a], [b]) => a.localeCompare(b));
  const num = sorted
    .filter(([, e]) => e > 0)
    .map(token)
    .join('·');
  const den = sorted
    .filter(([, e]) => e < 0)
    .map(token)
    .join('·');
  const numerator = num === '' ? '1' : num;
  return den === '' ? numerator : `${numerator}/${den}`;
};

/**
 * Format a dimension vector for display beside a value: registered units use their
 * canonical display, derived combinations compose generically, dimensionless renders blank.
 */
export const formatUnit = (unit: Unit): string =>
  isDimensionless(unit) ? '' : (registry.displayBySignature.get(signature(unit)) ?? composeGeneric(unit));

/**
 * Convert a value from one unit to another via their shared canonical base. Throws
 * when the two units do not share a dimension (per-unit-to-base keeps this O(n)).
 */
export const convert = (v: number, from: UnitDef, to: UnitDef): number => {
  if (!equals(from.dimension, to.dimension)) {
    throw new Error(`Cannot convert between incompatible dimensions: ${from.slug} → ${to.slug}`);
  }
  return to.fromBase(from.toBase(v));
};

/** Find the ladder whose rungs carry the given dimension, if any. */
const ladderForDimension = (dimension: Unit): readonly UnitDef[] | undefined =>
  Object.values(LADDERS).find((rungs) => {
    const first = rungs[0];
    return first !== undefined && equals(first.dimension, dimension);
  });

const dimensionOf = (dimensionOrUnit: Unit | UnitDef): Unit =>
  dimensionOrUnit instanceof Map ? dimensionOrUnit : (dimensionOrUnit as UnitDef).dimension;

/**
 * Whether the dimension carries a display ladder — i.e. {@link pickDisplay} will
 * scale a canonical-base value onto a magnitude rung (STB → MSTB → MMSTB, …) and
 * fold the magnitude into the petroleum unit prefix. Non-laddered dimensions
 * (pressure, temperature, money, …) keep a single fixed display symbol, so panels
 * must NOT route them through the unit-aware compaction path.
 */
export const isLaddered = (dimensionOrUnit: Unit | UnitDef): boolean =>
  ladderForDimension(dimensionOf(dimensionOrUnit)) !== undefined;

/**
 * Choose the {@link UnitDef} to display a canonical-base value as. Forced overrides
 * (`opts.target`/`opts.rung`) win; otherwise the dimension's ladder is walked and the
 * rung whose displayed mantissa falls in `[1, 1000)` is chosen, clamped to the
 * smallest/largest rung at the ends. `vBase === 0` yields the base (smallest) rung.
 */
export const pickDisplay = (
  vBase: number,
  dimensionOrUnit: Unit | UnitDef,
  opts?: { target?: UnitSlug; rung?: UnitSlug },
): UnitDef => {
  const forced = opts?.target ?? opts?.rung;
  if (forced !== undefined) {
    const def = registry.bySlug.get(forced);
    if (def === undefined) {
      throw new Error(`Unknown forced display unit: ${forced}`);
    }
    return def;
  }

  const dimension = dimensionOf(dimensionOrUnit);
  const ladder = ladderForDimension(dimension);
  if (ladder === undefined) {
    const def = dimensionOrUnit instanceof Map ? undefined : (dimensionOrUnit as UnitDef);
    if (def === undefined) {
      throw new Error(`No display ladder for dimension: ${signature(dimension)}`);
    }
    return def;
  }

  const largest = ladder.reduce((_, rung) => rung);
  const smallest = ladder[0] ?? largest;

  if (vBase === 0) {
    return smallest;
  }

  const inBand = ladder.find((rung) => {
    const mantissa = Math.abs(rung.fromBase(vBase));
    return mantissa >= 1 && mantissa < 1000;
  });
  if (inBand !== undefined) {
    return inBand;
  }

  // Below the smallest rung's band → smallest rung; above the largest → largest rung.
  return Math.abs(smallest.fromBase(vBase)) < 1 ? smallest : largest;
};
